import { ENV_CONFIG } from '../../../../__config__/env';
import { useMutation, useQuery } from '@apollo/client';
import React from 'react';
import styled from 'styled-components';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';
import { useScrollToElement } from '../../../../utils/hooks/useScrollToElement';
import { routeConfig } from '../../../../__config__/routes';

import { BackgroundLoad } from '../../../layout/BackgroundLoad';
import { Container } from '../../../layout/Container';
import { Section } from '../../../layout/Section';

import { Button } from '../../../ui/button/Button';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Form, FormRow, FormWrapper } from '../../../ui/form/Form';
import { FormError } from '../../../ui/form/FormError';
import { Input } from '../../../ui/form/Input';

import { ButtonLink } from '../../../ui/link/ButtonLink';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Thanks } from '../../../layout/Thanks';
import { AccordionGeneral } from '../../../partials/AccordionGeneral';
import { AccordionBenefits } from '../../../partials/AccordionBenefits';

import { ILeadInput, ILeadResponse } from '../../../../__types__/global';
import { MAIL_MUTATION } from '../../../../../client/__graphql__/mutations/mail';

import { HANS_CHRISTIAN_PROPERTY_QUERY } from '../../../../../client/__graphql__/queries';
import { animated, useSpring } from 'react-spring';
import { Link } from 'react-router-dom';
import { EmailOldIcon } from '../../../ui/icons/Email';
import { CarouselButtonRight } from '../../../ui/button/ButtonCarouselRight';
import {
  Image,
  ImageCarouselBulletsWithNoAnimation
} from '../../../ui/carousel/ImageCarouselBulletsWithNoAnimation';
import { CarouselButtonLeft } from '../../../ui/button/ButtonCarouselLeft';
import { Logo } from './Logo';
import PopupButton from './Popup';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

const HelgeLandsKysten: React.FC<IKvadrat> = () => {
  const formRef = React.useRef(null);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [posted, setPosted] = React.useState(false);
  const { scrollTo } = useScrollToElement();
  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const { submitWithDigger } = useDigtectiveContext();

  const [mail, { loading: mailLoading, error }] = useMutation<
    ILeadResponse,
    ILeadInput
  >(MAIL_MUTATION, {
    onCompleted: ({ lead }) => {
      if (lead && lead.mail && lead.mail.success) {
        setPosted(true);
      }
    }
  });

  const animatedOtherContent = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 }
  });

  const { data, loading: loadingCh } = useQuery(HANS_CHRISTIAN_PROPERTY_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      input: {
        broker: { id: 574 },
        pagination: { limit: 6, page }
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Ola Nordmann',
        value: '',
        label: 'Navn'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: '1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: '00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'textarea',
        name: 'comment',
        placeholder: 'Skriv kommentar',
        value: '',
        label: 'Kommentar'
      }
    ],

    submit: () => {
      const preparedFields: any = getFields(fields);
      setLoading(true);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        mail({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: 'EPOST',
              mailType: 103
            }
          }
        });
      });
    }
  });

  return (
    <HelgelandStyle>
      <Container className="firstIntro">
        <Wrapper>
          <Section
            className="form-section"
            style={{
              paddingBottom: '0'
            }}
          >
            <Logo
              name="Trondheim"
              alt="bilde av logo"
              style={{ margin: '0 auto', paddingTop: '100px' }}
            />
          </Section>

          <ImageCarousel />
          <PopupButton
            className="popup"
            onClick={() => {
              if (typeof scrollTo === 'function') {
                scrollTo({
                  ref: formRef,
                  distanceTop: 100,
                  behaviour: 'smooth'
                });
              }
            }}
          />

          <Paragraph center>
            Livet på Helgeland er formet av naturen på Helgeland. Lange, lyse,
            varme sommernetter under midnattssola. Korte, mørke og kalde
            vinterdager i mørketida. Titusenvis av øyer, holmer og skjær.
            Milevis til havs er hjem for sjøfugl, sel og kystfolk. Fjellene som
            reiser seg i karakteristiske formasjoner har inspirert til sagn og
            eventyr i flere tusen år. Unesco har gitt den unike kystkulturen
            verdensarvstatus. Dette gjør Helgelandskysten til et attraktivt sted
            å både feriere og bo.
          </Paragraph>
          <Container />

          <Heading tag="h3" center={true}>
            Kontakt meg om du skal selge din eiendom på Helgelandskysten
          </Heading>

          <Container>
            <Paragraph center>
              Jeg har vært så heldig å få jobbe med eiendom i hele 25 år. Jeg
              har også vært veldig heldig med å oppdage og bli kjent med
              Helgelandskysten. Med dette som bakgrunn, har jeg et godt
              utgangspunkt for å gi verdiskapende råd til salg- og
              kjøpsprosesser av både fritids- og bruktbolig på Helgelandskysten.
            </Paragraph>
            <Paragraph center>
              En av mine anbefalinger, er å komme i gang med forberedelser ved
              salg eller kjøp så tidlig som mulig. All erfaring viser at gode
              forberedelser og tilrettelegging før salg kan utgjøre mye på
              salgssummen. Ved å starte planleggingen tidlig, har man et større
              mulighetsrom til å gjøre riktige og viktige prioriteringer.
              Planlegger du salgsfremmende tiltak med megler tidlig, sikrer du
              at eiendommen fremstår på sitt aller beste når den skal selges.
            </Paragraph>

            <Paragraph center>
              Jeg skreddersyr leveransen gjennom salgsprosessen til enhver
              eiendom jeg selger. Dette har stor verdi for å vekke interesse hos
              potensielle kjøpere. PrivatMegleren har enerett i Norge på
              kraftfulle annonseverktøy for eiendom. Sammen med min kunnskap og
              mine råd, sikrer vi et best mulig resultat.
            </Paragraph>
            <Paragraph center>
              Kontakt meg gjerne dersom du har tanker om å selge eller kjøpe
              eiendom på Helgelandskysten.
            </Paragraph>
          </Container>

          <EvidenceSection style={animatedOtherContent}>
            <div className="section-container">
              <EvidenceCard>
                <EvidenceImage src="https://cdn.reeltime.no/pm_assets/kampanjer/trondheim/hans_christian.jpg" />
                <EvidenceCardInfo>
                  <a href="tel:94001660" className="phone">
                    94 00 16 60
                  </a>
                  <Link to={`/megler/hans-christian-espenes/kontakt`}>
                    <EmailOldIcon style={{ marginLeft: '20px' }} />
                  </Link>
                </EvidenceCardInfo>
              </EvidenceCard>
            </div>
          </EvidenceSection>
        </Wrapper>

        <FormWrapper style={fadeOut}>
          <Section
            ref={formRef}
            className="form-section"
            style={{
              paddingBottom: '10px',
              paddingTop: '0',
              fontFamily: 'Proxima Nova'
            }}
          >
            <Heading tag="h2" center={true}>
              Bli kontaktet
            </Heading>
            <Paragraph center={true} className="form-text">
              Fyll ut skjema, så tar vi en prat.
            </Paragraph>
          </Section>
          <Form
            onSubmit={handleSubmit}
            noValidate
            style={{ paddingBottom: '50px' }}
          >
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item, index) => {
              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item?.type}
                    name={item?.name}
                    label={item?.label}
                    labelAfter={item?.labelAfter}
                    placeholder={item?.placeholder}
                    value={item?.value}
                    onChange={handleChange}
                    error={item?.error}
                  />
                </FormRow>
              );
            })}
            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Kontakt meg
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
          </Form>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
            <Paragraph center={true}>
              En eiendomsmegler vil ta kontakt med deg så snart som mulig.
            </Paragraph>
            <AccordionGeneral />
          </Section>
          <Section className="form-section" style={{ paddingTop: 0 }}>
            <AccordionBenefits />
          </Section>
        </Thanks>
      </Container>

      <BackgroundLoad
        opacity={0.8}
        url={`${ENV_CONFIG.CDN_HOST}privatmegleren/helgelandskysten.jpg`}
        showOnMobile
      />
    </HelgelandStyle>
  );
};

const Properties = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 2em;
  grid-row-gap: 2em;
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  padding: 0 2rem;

  @media all and(max-width: 600px) {
    grid-template-columns: 1fr;
  }

  @media all and (min-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    grid-row-gap: 2em;
  }
`;

const HelgelandStyle = styled.div`
  .secondIntro {
    margin-top: 2em;
    font-family: 'Proxima Nova';
  }

  .no-margin {
    margin-top: 0;
  }
  @media all and (max-width: 600px) {
    .firstIntro {
      margin-top: 2em;
    }
  }

  @media all and (min-width: 600px) {
    .secondIntro {
      margin-top: 2em;
      font-family: 'Proxima Nova';
    }
    .no-margin {
      margin-top: 0;
    }
    .bottom {
      margin-top: -9em;
    }
    .firstIntro {
      margin-top: -3em;
    }
  }
`;
const Wrapper = styled.div`
  .pagination {
    padding: 2em 0 2em;
  }
`;
const EvidenceSection = styled(animated.section)`
  display: flex;
  width: 100%;
  padding: 25px 30px;
  /*   background: #23030a; */

  .section-container {
    display: grid;
    grid-template-columns: 1fr;
    grid-column-gap: 50px;
    max-width: 960px;
    width: 100%;
    margin: 0 auto;

    @media all and (max-width: 960px) {
      grid-template-columns: 1fr;
      grid-column-gap: 25px;
      grid-row-gap: 25px;
      width: 100%;
    }
  }
`;
const EvidenceCardInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: 20px;

  .phone {
    color: ${({ theme }) => theme.colors.primary};
    padding-bottom: 1em;
  }
`;

const EvidenceCard = styled.div`
  display: flex;
  flex-flow: column;
  /*  background: #1a0307; */
  padding: 20px;
`;

const EvidenceImage = styled.img`
  width: 100%;
  margin: 0;
`;

const ImageCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 < 0 ? array.length - 1 : prev - 1));
  };
  const next = () => {
    setCurrentIndex((prev) => (prev + 1 > array.length - 1 ? 0 : prev + 1));
  };

  const select = (index: number) => {
    setCurrentIndex(index);
  };
  const array = [
    {
      url: `${ENV_CONFIG.CDN_HOST}privatmegleren/kysten1_credited.jpg`
    },
    {
      url: `${ENV_CONFIG.CDN_HOST}privatmegleren/kysten2_credited.jpg`
    },
    {
      url: `${ENV_CONFIG.CDN_HOST}privatmegleren/kysten3_credited.jpg`
    }
  ];
  return (
    <>
      <StyledCarouselWrapper>
        <CarouselSection>
          <Container>
            <CarouselImageWrapper>
              <CarouselButtonRight className="right" onClick={prev} />

              {array.map((item, index) => {
                if (index === currentIndex) {
                  return (
                    <ImageWrapper key={index}>
                      <Image className="img" key={index} url={item.url} />
                    </ImageWrapper>
                  );
                } else {
                  return null;
                }
              })}
              <ImageCarouselBulletsWithNoAnimation
                className="bullets"
                images={array}
                select={select}
                currentIndex={currentIndex}
              />
              <CarouselButtonLeft className="left" onClick={next} />
            </CarouselImageWrapper>
          </Container>
        </CarouselSection>
      </StyledCarouselWrapper>
    </>
  );
};

const StyledCarouselWrapper = styled.div`
  @media all and (max-width: 1000px) {
    .left  {
      padding-right: 4em;
    }
    .right {
      padding-left: 4em;
    }
  }

  .left  {
    right: -70px;
  }
  .right {
    left: -70px;
  }
  .bullets {
    position: relative;
    width: 100%;
    max-width: 400px;
  }
`;

const CarouselSection = styled(animated.div)`
  display: block;
  text-align: center;
`;

const CarouselImageWrapper = styled.div`
  position: relative;
  display: block;
`;

const ImageWrapper = styled.div`
  img {
    height: 39.9375em;
  }
`;

interface IKvadrat {}

export default HelgeLandsKysten;
